/**
 * Client-side Error Tracking Service
 *
 * Captures errors from:
 * - React Error Boundaries
 * - Window.onerror (uncaught exceptions)
 * - Unhandled Promise Rejections
 * - Console.error calls
 *
 * Sends errors to /api/v1/errors/log endpoint for centralized tracking.
 */

import { auth } from '../lib/firebase';

// Trim API_BASE to prevent issues with trailing whitespace/newlines in env vars
const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3001').trim();

// Session ID persists for the browser session
let sessionId: string | null = null;

function getSessionId(): string {
  if (!sessionId) {
    // Try to get from sessionStorage first (persists across reloads)
    sessionId = sessionStorage.getItem('error_session_id');
    if (!sessionId) {
      sessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('error_session_id', sessionId);
    }
  }
  return sessionId;
}

// Breadcrumb trail for context
interface Breadcrumb {
  type: 'click' | 'navigation' | 'network' | 'console' | 'custom';
  message: string;
  timestamp: number;
  data?: Record<string, unknown>;
}

const breadcrumbs: Breadcrumb[] = [];
const MAX_BREADCRUMBS = 50;

function addBreadcrumb(crumb: Omit<Breadcrumb, 'timestamp'>): void {
  breadcrumbs.push({ ...crumb, timestamp: Date.now() });
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.shift();
  }
}

// Get user context
function getUserContext(): { userId?: string; userEmail?: string } {
  const user = auth.currentUser;
  if (user) {
    return {
      userId: user.uid,
      userEmail: user.email || undefined,
    };
  }
  return {};
}

// Get device/browser info
function getDeviceInfo(): {
  browser: string;
  deviceType: string;
  userAgent: string;
} {
  const ua = navigator.userAgent;
  let browser = 'unknown';
  let deviceType = 'desktop';

  // Detect browser
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Edge')) browser = 'Edge';

  // Detect device type
  if (/Mobi|Android/i.test(ua)) deviceType = 'mobile';
  else if (/Tablet|iPad/i.test(ua)) deviceType = 'tablet';

  return { browser, deviceType, userAgent: ua };
}

// Get current route
function getCurrentRoute(): string {
  return window.location.pathname + window.location.search;
}

// Prevent infinite loops - don't log errors from error logging
let isLogging = false;
// True while the ID token for a log request is being fetched. The Firebase SDK can call console.error from
// inside that (async) fetch, after `isLogging` has already been released; without this flag that error would be
// logged, fetch another token, fail again, and so on.
let isFetchingToken = false;

// App version from build (if available)
const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'dev';

// Branch ID for multi-tenant context
function getBranchId(): string | undefined {
  try {
    const branchStore = sessionStorage.getItem('branch-storage');
    if (branchStore) {
      const parsed = JSON.parse(branchStore);
      return parsed.state?.selectedBranchId || undefined;
    }
  } catch {
    // Ignore
  }
  return undefined;
}

export type CapturePoint =
  | 'error_boundary'
  | 'window_onerror'
  | 'unhandled_rejection'
  | 'console_error'
  | 'console_warn'
  | 'manual';

export interface ErrorEventPayload {
  message: string;
  name?: string;
  stack?: string;
  severity?: 'critical' | 'error' | 'warning' | 'info';
  category?: string;
  source: 'client';
  capturePoint: CapturePoint;
  userId?: string;
  userEmail?: string;
  sessionId: string;
  currentRoute: string;
  breadcrumbs: Breadcrumb[];
  browser: string;
  deviceType: string;
  appVersion: string;
  userAgent: string;
  branchId?: string;
}

/**
 * Log an error to the backend.
 */
async function logError(payload: Omit<ErrorEventPayload, 'sessionId' | 'currentRoute' | 'breadcrumbs' | 'browser' | 'deviceType' | 'appVersion' | 'userAgent' | 'source'>): Promise<void> {
  // Prevent infinite loops
  if (isLogging || isFetchingToken) return;
  isLogging = true;

  try {
    const userContext = getUserContext();
    const deviceInfo = getDeviceInfo();

    const fullPayload: ErrorEventPayload = {
      ...payload,
      source: 'client',
      userId: userContext.userId,
      userEmail: userContext.userEmail,
      sessionId: getSessionId(),
      currentRoute: getCurrentRoute(),
      breadcrumbs: [...breadcrumbs],
      browser: deviceInfo.browser,
      deviceType: deviceInfo.deviceType,
      appVersion: APP_VERSION,
      userAgent: deviceInfo.userAgent,
      branchId: getBranchId(),
    };

    // Fire and forget - don't await, don't block. When the user is signed in the ID token is attached so the
    // server can record WHO from a verified identity (it ignores any userId/userEmail in the body). If there
    // is no token, or fetching it fails, the error is still sent, anonymously.
    void sendErrorEvent(fullPayload);
  } finally {
    isLogging = false;
  }
}

async function sendErrorEvent(payload: ErrorEventPayload): Promise<void> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    isFetchingToken = true;
    try {
      const token = await auth.currentUser?.getIdToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      // token unavailable: send anonymously
    } finally {
      isFetchingToken = false;
    }
    await fetch(`${API_BASE}/api/v1/errors/log`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
  } catch {
    // Silently fail - we can't log errors about error logging
  }
}

/**
 * Capture a JavaScript Error object.
 */
export function captureError(
  error: Error,
  capturePoint: CapturePoint = 'manual',
  extraContext?: Record<string, unknown>
): void {
  logError({
    message: error.message,
    name: error.name,
    stack: error.stack,
    capturePoint,
    ...(extraContext && { category: extraContext.category as string }),
  });
}

/**
 * Capture a message as an error.
 */
export function captureMessage(
  message: string,
  severity: 'critical' | 'error' | 'warning' | 'info' = 'info',
  capturePoint: CapturePoint = 'manual'
): void {
  logError({
    message,
    severity,
    capturePoint,
  });
}

/**
 * Add a breadcrumb to the trail.
 */
export function addErrorBreadcrumb(
  type: Breadcrumb['type'],
  message: string,
  data?: Record<string, unknown>
): void {
  addBreadcrumb({ type, message, data });
}

/**
 * Safely serialize any value for error logging
 * Handles Error objects, DOM exceptions, and other non-serializable objects
 */
function safeSerialize(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  // Handle Error objects specially
  if (value instanceof Error) {
    return `${value.name}: ${value.message}${value.stack ? `\n${value.stack}` : ''}`;
  }

  // Try JSON.stringify first
  try {
    const stringified = JSON.stringify(value);
    // If it stringified to an empty object, try extracting properties manually
    if (stringified === '{}' && typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      const props = Object.getOwnPropertyNames(obj);
      if (props.length > 0) {
        const propsStr = props.map(key => `${key}: ${safeSerialize(obj[key])}`).join(', ');
        return `{${propsStr}}`;
      }
    }
    return stringified;
  } catch {
    // Fallback for circular references or other issues
    return String(value);
  }
}

// Track if already initialized
let initialized = false;

/**
 * Initialize global error handlers.
 * Call this once at app startup.
 */
export function initializeErrorTracking(): void {
  if (initialized) return;
  initialized = true;

  // Store original console methods
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;

  // Hook console.error
  console.error = (...args: unknown[]): void => {
    originalConsoleError.apply(console, args);

    // Don't capture React's internal error logging (it duplicates error boundary captures)
    const message = args.map(arg =>
      typeof arg === 'string' ? arg : safeSerialize(arg)
    ).join(' ');

    // Skip React's error overlay messages
    if (message.includes('The above error occurred in') ||
        message.includes('Consider adding an error boundary')) {
      return;
    }

    addBreadcrumb({ type: 'console', message: `console.error: ${message.slice(0, 200)}` });

    // Only log as error if it looks like a real error
    if (args[0] instanceof Error) {
      captureError(args[0], 'console_error');
    } else if (!message.includes('[HMR]') && !message.includes('[vite]')) {
      logError({
        message: message.slice(0, 1000),
        capturePoint: 'console_error',
      });
    }
  };

  // Hook console.warn (sampled at lower rate on backend)
  console.warn = (...args: unknown[]): void => {
    originalConsoleWarn.apply(console, args);

    const message = args.map(arg =>
      typeof arg === 'string' ? arg : safeSerialize(arg)
    ).join(' ');

    // Skip dev tooling warnings
    if (message.includes('[HMR]') || message.includes('[vite]')) {
      return;
    }

    addBreadcrumb({ type: 'console', message: `console.warn: ${message.slice(0, 200)}` });
  };

  // Global error handler (uncaught exceptions)
  window.onerror = (message, source, lineno, colno, error): boolean => {
    addBreadcrumb({
      type: 'custom',
      message: `Uncaught error: ${message}`,
      data: { source, lineno, colno },
    });

    if (error) {
      captureError(error, 'window_onerror');
    } else {
      logError({
        message: String(message),
        capturePoint: 'window_onerror',
        stack: `at ${source}:${lineno}:${colno}`,
      });
    }

    // Return false to allow default browser handling
    return false;
  };

  // Unhandled promise rejection handler
  window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
    const error = event.reason;

    addBreadcrumb({
      type: 'custom',
      message: `Unhandled rejection: ${error?.message || String(error)}`,
    });

    if (error instanceof Error) {
      captureError(error, 'unhandled_rejection');
    } else {
      logError({
        message: String(error),
        capturePoint: 'unhandled_rejection',
      });
    }
  };

  // Track navigation
  const originalPushState = history.pushState;
  history.pushState = function(...args): void {
    originalPushState.apply(this, args);
    addBreadcrumb({
      type: 'navigation',
      message: `Navigate to ${window.location.pathname}`,
    });
  };

  // Track clicks for context
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    if (!target) return;

    // Get a useful identifier for the clicked element
    let identifier = target.tagName.toLowerCase();
    if (target.id) identifier = `#${target.id}`;
    else if (target.className && typeof target.className === 'string') {
      const mainClass = target.className.split(' ')[0];
      if (mainClass) identifier = `.${mainClass}`;
    }

    // Get text content for buttons/links
    let text = '';
    if (target.textContent) {
      text = target.textContent.trim().slice(0, 30);
    }

    addBreadcrumb({
      type: 'click',
      message: `Click on ${identifier}${text ? `: "${text}"` : ''}`,
    });
  }, { capture: true, passive: true });

  // Log successful initialization
  addBreadcrumb({
    type: 'custom',
    message: 'Error tracking initialized',
  });
}

/**
 * Capture an error from React Error Boundary.
 */
export function captureErrorBoundary(
  error: Error,
  errorInfo: { componentStack?: string; sectionName?: string }
): void {
  const sectionPrefix = errorInfo.sectionName ? `[${errorInfo.sectionName}] ` : '';
  logError({
    message: sectionPrefix + error.message,
    name: error.name,
    stack: error.stack + (errorInfo.componentStack ? `\n\nComponent Stack:${errorInfo.componentStack}` : ''),
    capturePoint: 'error_boundary',
    severity: 'critical',
    category: 'react',
  });
}

/**
 * Capture a network error for context.
 */
export function captureNetworkError(
  url: string,
  method: string,
  status: number,
  error?: string
): void {
  addBreadcrumb({
    type: 'network',
    message: `${method} ${url} → ${status}${error ? `: ${error}` : ''}`,
    data: { url, method, status, error },
  });

  // Only log 5xx errors as actual errors
  if (status >= 500) {
    logError({
      message: `Network error: ${method} ${url} returned ${status}`,
      capturePoint: 'manual',
      category: 'network',
    });
  }
}
